import { createFileRoute, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Suspense } from "react";
import { CheckCircle2, XCircle, AlertTriangle, MinusCircle, RefreshCw, Trash2 } from "lucide-react";

import { SiteHeader } from "@/components/site/SiteHeader";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getIntegrationsHealth,
  clearBokunCacheFn,
  type HealthStatus,
  type IntegrationCheck,
} from "@/lib/integrations-health.functions";

const healthQuery = queryOptions({
  queryKey: ["admin", "integrations-health"],
  queryFn: () => getIntegrationsHealth(),
  staleTime: 0,
});

export const Route = createFileRoute("/_authenticated/admin/integrations")({
  head: () => ({
    meta: [
      { title: "Integraciones — Estado de salud" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(healthQuery),
  component: IntegrationsPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-center text-sm text-destructive">{error instanceof Error ? error.message : String(error)}</div>
  ),
});

function IntegrationsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div>
          <h1 className="font-display text-4xl font-semibold">Integraciones · Salud</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Verificación en vivo de Bokun, pagos, IA, vuelos y base de datos. Última sincronización,
            frescura del caché y métricas recientes.
          </p>
        </div>
        <div className="mt-6"><AdminNav /></div>
        <Suspense fallback={<Skeleton className="mt-8 h-96 w-full rounded-2xl" />}>
          <Body />
        </Suspense>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: HealthStatus }) {
  if (status === "ok")
    return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Operativo</Badge>;
  if (status === "degraded")
    return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300"><AlertTriangle className="mr-1 h-3.5 w-3.5" />Degradado</Badge>;
  if (status === "down")
    return <Badge variant="destructive"><XCircle className="mr-1 h-3.5 w-3.5" />Caído</Badge>;
  return <Badge variant="secondary"><MinusCircle className="mr-1 h-3.5 w-3.5" />Sin configurar</Badge>;
}

function fmtTime(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function fmtRelative(iso: string | null) {
  if (!iso) return "nunca";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return `en ${Math.abs(Math.round(diff / 1000))}s`;
  if (diff < 60_000) return `hace ${Math.round(diff / 1000)}s`;
  if (diff < 3_600_000) return `hace ${Math.round(diff / 60_000)} min`;
  if (diff < 86_400_000) return `hace ${Math.round(diff / 3_600_000)} h`;
  return `hace ${Math.round(diff / 86_400_000)} d`;
}

function CheckCard({ c }: { c: IntegrationCheck }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>{c.name}</span>
          <StatusBadge status={c.status} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-muted-foreground">{c.message}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>Verificado: {fmtRelative(c.lastCheckedAt)}</span>
          {c.elapsedMs > 0 && <span>RTT: {c.elapsedMs} ms</span>}
        </div>
        {Object.keys(c.details).length > 0 && (
          <dl className="mt-2 grid gap-1 rounded-md bg-muted/40 p-3 text-xs">
            {Object.entries(c.details).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="break-all text-right font-mono">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function Body() {
  const router = useRouter();
  const { data } = useSuspenseQuery(healthQuery);
  const clearCache = useServerFn(clearBokunCacheFn);
  const clearMutation = useMutation({
    mutationFn: () => clearCache(),
    onSuccess: () => router.invalidate(),
  });

  return (
    <div className="mt-8 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Estado global</span>
            <StatusBadge status={data.overall} />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Generado {fmtRelative(data.generatedAt)} · {fmtTime(data.generatedAt)}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => router.invalidate()}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-verificar todo
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.checks.map((c) => (
          <CheckCard key={c.id} c={c} />
        ))}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
          <div>
            <CardTitle className="text-base">Bokun · Sincronización y caché</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Último fetch exitoso, frescura del caché en memoria y métricas acumuladas desde el último arranque.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isPending}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            {clearMutation.isPending ? "Limpiando…" : "Vaciar caché"}
          </Button>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-2">
          <div>
            <h3 className="text-sm font-medium">Último sync</h3>
            <dl className="mt-2 space-y-1 text-sm">
              <Row label="Último fetch OK">{fmtTime(data.lastBokunSuccessAt)} <span className="text-xs text-muted-foreground">({fmtRelative(data.lastBokunSuccessAt)})</span></Row>
              <Row label="Verificado">{fmtRelative(data.generatedAt)}</Row>
            </dl>
            <h3 className="mt-5 text-sm font-medium">Métricas acumuladas</h3>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <Row label="Solicitudes">{data.bokunMetrics.requests}</Row>
              <Row label="Intentos">{data.bokunMetrics.attempts}</Row>
              <Row label="Reintentos">{data.bokunMetrics.retries}</Row>
              <Row label="Rate-limited">{data.bokunMetrics.rateLimited}</Row>
              <Row label="Errores 5xx">{data.bokunMetrics.serverErrors}</Row>
              <Row label="Errores de red">{data.bokunMetrics.networkErrors}</Row>
              <Row label="Fallos">{data.bokunMetrics.failures}</Row>
            </dl>
          </div>
          <div>
            <h3 className="text-sm font-medium">Caché en memoria (LRU + SWR)</h3>
            <dl className="mt-2 space-y-1 text-sm">
              <Row label="Entradas">{data.bokunCache.entries} / {data.bokunCache.maxEntries}</Row>
              <Row label="Frescas">{data.bokunCache.freshEntries}</Row>
              <Row label="Stale (SWR)">{data.bokunCache.staleEntries}</Row>
              <Row label="Expiradas">{data.bokunCache.expiredEntries}</Row>
              <Row label="Caduca primero">{fmtTime(data.bokunCache.oldestExpiresAt)}</Row>
              <Row label="Caduca último">{fmtTime(data.bokunCache.newestExpiresAt)}</Row>
            </dl>
            <h3 className="mt-5 text-sm font-medium">Rendimiento del caché</h3>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <Row label="Hit rate">{(data.bokunCache.hitRate * 100).toFixed(1)}%</Row>
              <Row label="Hits">{data.bokunCache.hits}</Row>
              <Row label="Stale served">{data.bokunCache.staleHits}</Row>
              <Row label="Misses">{data.bokunCache.misses}</Row>
              <Row label="Expiraciones">{data.bokunCache.expirations}</Row>
              <Row label="Evictions (LRU)">{data.bokunCache.evictions}</Row>
              <Row label="Escrituras">{data.bokunCache.writes}</Row>
              <Row label="Revalidaciones">{data.bokunCache.revalidations}</Row>
              <Row label="Reval. fallidas">{data.bokunCache.revalErrors}</Row>
            </dl>
            <h3 className="mt-5 text-sm font-medium">Tiempos de respuesta ({data.bokunCache.rtt.samples} muestras)</h3>
            <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <Row label="Promedio">{data.bokunCache.rtt.avgMs} ms</Row>
              <Row label="p50">{data.bokunCache.rtt.p50Ms} ms</Row>
              <Row label="p95">{data.bokunCache.rtt.p95Ms} ms</Row>
              <Row label="Máx">{data.bokunCache.rtt.maxMs} ms</Row>
              <Row label="Lentas (>2s)">{data.bokunCache.rtt.slowCount}</Row>
            </dl>
            <p className="mt-3 rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
              LRU con cap de {data.bokunCache.maxEntries}. SWR sirve resultados vencidos durante 5 min y refresca en segundo plano. El caché se reinicia con cada despliegue.
            </p>
          </div>
        </CardContent>
      </Card>

      {data.bokunCache.slowestPaths.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Endpoints más lentos</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">Promedio y máximo por ruta (excluye query string). Marca las que superan 2s como candidatas a caché más agresiva.</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1.5 pr-3 font-medium">Path</th>
                    <th className="py-1.5 pr-3 font-medium">Llamadas</th>
                    <th className="py-1.5 pr-3 font-medium">Promedio</th>
                    <th className="py-1.5 pr-3 font-medium">Máx</th>
                    <th className="py-1.5 pr-3 font-medium">Lentas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bokunCache.slowestPaths.map((p) => (
                    <tr key={p.path} className="border-t">
                      <td className="py-1.5 pr-3 font-mono">{p.path}</td>
                      <td className="py-1.5 pr-3">{p.count}</td>
                      <td className={`py-1.5 pr-3 ${p.avgMs >= 2000 ? "text-amber-600 font-medium" : ""}`}>{p.avgMs} ms</td>
                      <td className="py-1.5 pr-3">{p.maxMs} ms</td>
                      <td className="py-1.5 pr-3">{p.slowCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Intentos recientes a Bokun</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">Últimos 20 intentos, más recientes arriba.</p>
        </CardHeader>
        <CardContent>
          {data.lastBokunAttempts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay intentos registrados aún.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1.5 pr-3 font-medium">Hora</th>
                    <th className="py-1.5 pr-3 font-medium">Método</th>
                    <th className="py-1.5 pr-3 font-medium">Path</th>
                    <th className="py-1.5 pr-3 font-medium">HTTP</th>
                    <th className="py-1.5 pr-3 font-medium">Resultado</th>
                    <th className="py-1.5 pr-3 font-medium">RTT</th>
                    <th className="py-1.5 pr-3 font-medium">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lastBokunAttempts.map((a, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-1.5 pr-3 font-mono">{fmtTime(a.ts)}</td>
                      <td className="py-1.5 pr-3">{a.method}</td>
                      <td className="py-1.5 pr-3 font-mono">{a.path}</td>
                      <td className="py-1.5 pr-3">{a.status ?? "—"}</td>
                      <td className="py-1.5 pr-3">
                        {a.outcome === "ok" ? (
                          <span className="text-emerald-600">ok</span>
                        ) : a.outcome === "retry" ? (
                          <span className="text-amber-600">retry</span>
                        ) : (
                          <span className="text-destructive">error</span>
                        )}
                      </td>
                      <td className="py-1.5 pr-3">{a.elapsedMs} ms</td>
                      <td className="py-1.5 pr-3 text-muted-foreground">{a.reason ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}
